'use client'

import { RefObject, useEffect, useRef } from 'react'

export type PlaybackSnapshot = {
  key: string
  time: number
  paused: boolean
}

export type RestorePlaybackOptions = {
  /**
   * 同じ動画かどうかを判定するキー。動画URLやノードキーなど、読み込み直しても
   * 変わらない値を渡す。
   *
   * 省略すると `currentSrc` を使うが、hls.js 経由（HLS変換済みの動画）では
   * `currentSrc` が `blob:...` になり、**読み込み直しのたびに別のURLへ変わる**。
   * その状態だと「同じ動画」と判定できず復元がスキップされるため、HLSを通す
   * プレイヤーでは必ず渡すこと。
   */
  mediaKey?: string
  /**
   * 要素が差し替わっても控えを残したいときに渡す。PC/SP を別コンポーネントで
   * 出し分ける VideoModal のように <video> ごと入れ替わる場所では、控えを親が
   * 持たないと引き継げない。省略時はフック内部に持つ。
   */
  store?: RefObject<PlaybackSnapshot | null>
  /** 要素が差し替わる契機。値が変わるとリスナーを貼り直す。 */
  resetToken?: unknown
}

/**
 * 同じ動画が読み込み直されたとき、直前の再生位置と再生/停止状態へ戻す。
 *
 * ブラウザは別タブへ移っているあいだにメディアを解放することがあり、戻ってきた
 * ときに <video> が読み込み直される。何もしないと
 *
 * - 視聴者が一時停止した動画が 0:00 のサムネイルに戻る
 * - `autoplay` が再武装されて（HTML仕様の media load algorithm）勝手に再生される
 *
 * が起きる。読み込み直しの前後で位置と再生/停止を引き継ぎ、「止めたところから
 * そのまま戻れる」状態にする。
 *
 * 別の動画へ切り替わったとき（mediaKey が変わったとき）は復元しない。
 * 初回の読み込みでは控えが無いので何もせず、自動再生の妨げにならない。
 */
export function useRestorePlaybackOnReload(
  videoRef: RefObject<HTMLVideoElement | null>,
  options: RestorePlaybackOptions = {},
): void {
  const { mediaKey, store, resetToken } = options
  const internalStore = useRef<PlaybackSnapshot | null>(null)

  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    const snapshot = store ?? internalStore
    const keyOf = () => mediaKey ?? video.currentSrc

    /**
     * 読み込み直しが始まってから復元するまでは控えを更新しない。
     * `emptied` の直後に currentTime 0 の `timeupdate` が来るため、素直に記録すると
     * 控えが 0 で潰れて復元できなくなる（実測で確認済み）。
     */
    let reloading = false

    const record = () => {
      const key = keyOf()
      if (reloading || !key) return
      snapshot.current = { key, time: video.currentTime, paused: video.paused }
    }

    const markReloading = () => {
      reloading = true
    }

    const restore = () => {
      reloading = false
      const saved = snapshot.current
      // 同じ動画が読み込み直された場合だけ戻す（動画の切り替えには介入しない）
      if (!saved || saved.key !== keyOf() || saved.time <= 0) return
      // すでに戻し終えていれば何もしない（canplay で二度目が来ても無害にする）
      if (Math.abs(video.currentTime - saved.time) < 0.25 && video.paused === saved.paused) return
      try {
        video.currentTime = saved.time
      } catch {
        // seek できない状態なら何もしない
        return
      }
      if (saved.paused) {
        video.pause()
      } else {
        void video.play().catch(() => undefined)
      }
    }

    // 差し替え直後にメタデータが揃っていれば、その場で戻す
    if (video.readyState >= 1) {
      restore()
    }

    video.addEventListener('emptied', markReloading)
    video.addEventListener('loadstart', markReloading)
    video.addEventListener('timeupdate', record)
    video.addEventListener('play', record)
    video.addEventListener('pause', record)
    video.addEventListener('seeked', record)
    // 読み込み直しは何度でも起こりうるので once にしない。
    // hls.js 経由だと loadedmetadata より後に再生が始まることがあるため canplay でも戻す。
    video.addEventListener('loadedmetadata', restore)
    video.addEventListener('canplay', restore)
    return () => {
      // 外し際の状態を控える（要素が差し替わるとき、次の要素へ引き継ぐ値になる）
      record()
      video.removeEventListener('emptied', markReloading)
      video.removeEventListener('loadstart', markReloading)
      video.removeEventListener('timeupdate', record)
      video.removeEventListener('play', record)
      video.removeEventListener('pause', record)
      video.removeEventListener('seeked', record)
      video.removeEventListener('loadedmetadata', restore)
      video.removeEventListener('canplay', restore)
    }
  }, [videoRef, mediaKey, store, resetToken])
}

export default useRestorePlaybackOnReload
